import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { ProposedCandidate, RoadRoute, TourProposal } from '../types';
import RouteMap from './RouteMap';

interface Props {
  proposal: TourProposal;
  /** Ključevi stanica koje su trenutno na turi. */
  selected: string[];
  keyOf: (stop: { kind: string; documentKey: string | null; locationKind: string | null; locationCode: string | null }) => string | null;
  /** Pozicija je mesto na koje stanica pripada; bez nje bi zavrsila na kraju ture. */
  onToggle: (key: string, position?: number) => void;
  onClose: () => void;
}

/**
 * Predlog ture za izabrani pravac: mapa i spisak.
 *
 * Crvene čiode su ono što ide, plave ono što se nudi usput. Plava nosi broj
 * kilometara koje dodaje, jer je to jedini broj po kom se odlučuje — blizina
 * sama ne kaže ništa o ceni.
 */
/** „3h 54min" umesto „234 min" — sati se čitaju, minuti se računaju. */
function formatDuration(minutes: number | null): string {
  if (minutes === null) return '';

  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);

  return hours > 0 ? `${hours}h ${rest}min` : `${rest} min`;
}

export default function ProposalPanel({ proposal, selected, keyOf, onToggle, onClose }: Props) {
  const [fullscreen, setFullscreen] = useState(false);

  /** Preračunat put, kad tura više nije ono što je predlog predložio. */
  const [road, setRoad] = useState<RoadRoute | null>(null);
  const [recomputing, setRecomputing] = useState(false);

  // Escape zatvara pun ekran. Bez toga se iz njega izlazi samo mišem, a mapa
  // pokriva ceo prozor pa nema šta drugo da se klikne.
  useEffect(() => {
    if (!fullscreen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const isSelected = (stop: Parameters<typeof keyOf>[0]) => {
    const key = keyOf(stop);
    return key !== null && selected.includes(key);
  };

  // Mapa prati trenutni izbor, ne prvobitni predlog: izbačena stanica se vraća
  // među plave, da operater može da se predomisli.
  const everything = [
    ...proposal.stops.map((stop) => ({ stop, candidate: null as ProposedCandidate | null })),
    ...proposal.candidates.map((candidate) => ({ stop: candidate.stop, candidate }))
  ];

  // Redosled na mapi je redosled ture, ne redosled predloga. Ranije su brojevi
  // pratili spisak iz predloga pa spisak kandidata, pa je dodata stanica uvek
  // dobijala poslednji broj — ma gde na turi stvarno bila.
  const onTour = selected
    .map((key) => everything.find((e) => keyOf(e.stop) === key))
    .filter((e): e is (typeof everything)[number] => e !== undefined)
    .map((e) => e.stop);

  const offered = everything.filter((e) => !isSelected(e.stop));

  const offeredCandidates: ProposedCandidate[] = offered.map(
    (e) =>
      e.candidate ?? {
        stop: e.stop,
        // Stanica iz predloga koju je operater izbacio nema računatu cenu —
        // bila je na pravcu, pa se prikazuje bez broja.
        distanceFromCorridorKm: 0,
        addedKm: 0,
        suggestedPosition: 0
      }
  );

  /**
   * Na koje mesto u turi stanica pripada.
   *
   * Za kandidata je to izračunato mesto najjeftinijeg ubacivanja. Backend ga
   * broji nad rutom koja počinje polazištem, a ovde se broji samo po
   * stanicama — otud −1.
   *
   * Stanica koju je operater izbacio pa se predomislio nema računatu cenu
   * (nula). Nju vraćamo tamo gde je bila u predlogu; bez toga bi jedan
   * pogrešan klik odbacio adresu na kraj ture.
   */
  const positionOf = (candidate: ProposedCandidate): number | undefined => {
    if (candidate.suggestedPosition > 0) return candidate.suggestedPosition - 1;

    const key = keyOf(candidate.stop);
    const original = key === null ? -1 : proposal.stops.findIndex((s) => keyOf(s) === key);
    if (original < 0) return undefined;

    // Broji se među stanicama koje su OSTALE na turi, ne u prvobitnom spisku.
    return proposal.stops.slice(0, original).filter(isSelected).length;
  };

  // Put predloga važi samo dok ga operater ne dodirne. Čim doda ili izbaci
  // stanicu, i nacrtana linija i „77 km" govore o turi koja više ne postoji —
  // a po tom broju se odlučuje da li tura staje u smenu. Zato se preračunava.
  const tourKey = onTour.map((s) => `${s.latitude},${s.longitude}`).join(';');
  const proposedKey = proposal.stops.map((s) => `${s.latitude},${s.longitude}`).join(';');

  useEffect(() => {
    if (tourKey === proposedKey) {
      setRoad(null);
      setRecomputing(false);
      return;
    }

    if (onTour.length === 0) {
      setRoad({ roadKm: null, driveMinutes: null, roadGeometry: [] });
      return;
    }

    setRecomputing(true);
    let cancelled = false;

    // Odloženo, da klikanje po mapi ne šalje poziv za svaku čiodu.
    const timer = window.setTimeout(() => {
      const points = [proposal.start, ...onTour, proposal.end].map((p) => ({
        latitude: p.latitude,
        longitude: p.longitude,
        label: p.label
      }));

      api
        .road(points)
        .then((result) => {
          if (!cancelled) setRoad(result);
        })
        .catch(() => {
          // Bez puta se crtaju prave linije, kao i pre nego što je rutiranja bilo.
          if (!cancelled) setRoad({ roadKm: null, driveMinutes: null, roadGeometry: [] });
        })
        .finally(() => {
          if (!cancelled) setRecomputing(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // Zavisi se od otiska ture, ne od samog niza — niz je nov pri svakom
    // iscrtavanju, pa bi se poziv slao u krug.
  }, [tourKey, proposedKey]);

  const roadKm = road ? road.roadKm : proposal.roadKm;
  const driveMinutes = road ? road.driveMinutes : proposal.driveMinutes;
  const roadGeometry = road ? road.roadGeometry : proposal.roadGeometry ?? [];

  const map = (
    <RouteMap
      start={proposal.start}
      end={proposal.end}
      corridor={proposal.corridor}
      stops={onTour}
      candidates={offeredCandidates}
      roadGeometry={roadGeometry}
      onAdd={(candidate) => {
        const key = keyOf(candidate.stop);
        if (key) onToggle(key, positionOf(candidate));
      }}
      onRemove={(stop) => {
        const key = keyOf(stop);
        if (key) onToggle(key);
      }}
      sizeToken={fullscreen ? 'pun' : 'panel'}
    />
  );

  if (fullscreen) {
    return (
      <div className="mapfull">
        <div className="mapfull__bar">
          <span>
            <strong>{proposal.templateName}</strong>
            <span className="muted">
              {' '}· {onTour.length} stanica · {offeredCandidates.length} predloga
            </span>
          </span>
          <button type="button" onClick={() => setFullscreen(false)}>Zatvori (Esc)</button>
        </div>
        {map}
      </div>
    );
  }

  return (
    <div className="proposal">
      <div className="proposal__head">
        <h3>{proposal.templateName}</h3>
        <span className="proposal__actions">
          <button type="button" onClick={() => setFullscreen(true)}>Uvećaj mapu</button>
          <button type="button" className="link" onClick={onClose}>Zatvori predlog</button>
        </span>
      </div>

      {!proposal.candidatesAreComplete && (
        <p className="blocker">
          Pojas oko pravca se ne računa dok se mesta ne potvrde na mapi. Nepotvrđeno:{' '}
          {proposal.unverifiedPlaces.join(', ')}.
        </p>
      )}

      <p className="muted proposal__path">
        {proposal.corridor.map((p) => p.label).join(' → ')}
      </p>

      {/* Izmereni put, kad je servis za rutiranje podešen. Bez njega se ovo ne
          prikazuje — bolje ništa nego broj koji izgleda tačno a nije. */}
      {roadKm !== null && (
        <p className="explanation">
          Stvarni put {roadKm.toFixed(0)} km, {formatDuration(driveMinutes)} vožnje.
          {recomputing && <span className="muted"> · preračunava se…</span>}
        </p>
      )}

      {map}

      <p className="muted map__hint">
        Crveno ide na turu, plavo se nudi — klik na plavu čiodu je dodaje,
        Ctrl+klik na crvenu je izbacuje. Izbačena postaje plava, pa se jednim
        klikom vraća tamo gde je bila. Zumiranje je Ctrl + točak.
      </p>

      {offeredCandidates.length > 0 && (
        <>
          <p className="proposal__title">
            Usput, po ceni obilaska{' '}
            <span className="muted">
              · nudi se sve do {proposal.beltKm.toFixed(0)} km u stranu od pravca
            </span>
          </p>

          <ul className="candidates">
            {offeredCandidates
              .slice()
              .sort((a, b) => a.addedKm - b.addedKm)
              .map((candidate) => {
                const key = keyOf(candidate.stop);
                return (
                  <li key={key ?? candidate.stop.label}>
                    <button
                      type="button"
                      disabled={!key}
                      onClick={() => key && onToggle(key, positionOf(candidate))}
                    >
                      <span className="candidates__km">+{candidate.addedKm.toFixed(1)} km</span>
                      <span className="candidates__where">
                        {candidate.stop.label}
                        {candidate.stop.city ? `, ${candidate.stop.city}` : ''}
                      </span>
                      <span className="muted candidates__meta">
                        {candidate.stop.pieces} kom · čeka {candidate.stop.waitingDays} d
                        {candidate.stop.requiresTwoPeople && ' · dva čoveka'}
                        {/* Isporuka čija roba nije u magacinu ne može ranije od
                            mesta gde se roba diže — to je razlog, ne slučaj. */}
                        {candidate.stop.pickupFromName
                          && ` · roba se uzima u ${candidate.stop.pickupFromName}`}
                      </span>
                    </button>
                  </li>
                );
              })}
          </ul>
        </>
      )}
    </div>
  );
}
